import React from 'react';
import ReactDOM from 'react-dom';

import {DataConfig, DataProvider} from '@sail/data';
import {ObservabilityConfig, ObservabilityProvider} from '@sail/observability';
import {Provider as SailThemeProvider} from '@sail/theme-classic';
import {RootLayer} from '@sail/ui';
import {
  getV1SchemaConfig,
  // eslint-disable-next-line camelcase
  unstable_getRevampedV1SchemaConfig,
} from '@stripe-internal/data-v1';
import type {Fetch} from '@sail/data';

import {getApiHost} from 'src/utils/buildStripeApiClient';
import {initCurrentScriptUrlContext} from 'src/utils/getCurrentScriptUrlContext';
import {LocalizationWrapper} from 'src/components/core/LocalizationWrapper';
import {initSentry} from 'src/utils/initSentry';
import {PayinsErrorBoundary} from 'src/components/core/PayinsErrorBoundary';
import {Router} from 'src/v2/state/routing';
import {ThemeProvider} from 'src/v2/state/theme/ThemeProvider';

const reactRoot = document.getElementById('react-root') as HTMLElement;

initCurrentScriptUrlContext();
initSentry();

// Default data staleness value.
const expireAfterMs = 30 * 1000;
const sharedCacheId = Symbol('crypto-payins-cache-id');

const useConfig = () => {
  return React.useMemo(() => {
    const obsConfig: ObservabilityConfig = {
      project: 'crypto_payins',
      service: 'crypto-payin',
      errors: {
        projects: {
          crypto_payins:
            process.env.NODE_ENV !== 'production'
              ? 'https://fc0b2ad507fe4334b90f0885b5482dca@errors.stripe.com/1239'
              : 'https://66e09dc56b844d8590a748f8ad4431db@errors.stripe.com/1237',
        },
        captureUnhandledErrors: true,
      },
      analytics: {
        disabled: true,
      },
      metrics: {
        disabled: true,
      },
    };

    const wrappedFetch: Fetch = async (url: string, options) => {
      const apiHost = getApiHost();
      const absoluteURL = new URL(url, `https://${apiHost}`);

      return fetch(absoluteURL.toString(), options);
    };

    const v1Config = getV1SchemaConfig([], {});

    const unstableV1Config = unstable_getRevampedV1SchemaConfig([], {});

    const unstableDataConfig: DataConfig['unstable_sail_data_revamp'] = {
      graphql: {
        sharedCacheId,
        schemas: [unstableV1Config],
        staleTime: expireAfterMs,
      },
      observability: {expectedErrors: []},
    };

    const dataConfig: DataConfig = {
      fetch: wrappedFetch,
      observability: {expectedApiErrors: [], mutedApiErrors: []},
      graphql: {
        sharedCacheId,
        // schemas contain {gql: <configuration for V1>}
        schemas: {
          ...v1Config,
        },
      },
      unstable_sail_data_revamp: unstableDataConfig,
    };

    return {obsConfig, dataConfig};
  }, []);
};

const Index = () => {
  const {obsConfig, dataConfig} = useConfig();
  return (
    <ObservabilityProvider {...obsConfig}>
      <DataProvider config={dataConfig}>
        <LocalizationWrapper>
          <PayinsErrorBoundary uuid="5P7LMI00">
            <SailThemeProvider>
              <RootLayer>
                <ThemeProvider>
                  <Router />
                </ThemeProvider>
              </RootLayer>
            </SailThemeProvider>
          </PayinsErrorBoundary>
        </LocalizationWrapper>
      </DataProvider>
    </ObservabilityProvider>
  );
};

ReactDOM.render(<Index />, reactRoot);
